import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { ArrowForward, CheckCircle, Upload } from '@mui/icons-material';
import * as cuitProfilesApi from '@/api/cuitProfiles';
import { useAuth } from '@/auth/useAuth';
import { LoadingScreen } from '@/components/LoadingScreen';
import { CuitProfileForm } from '@/components/CuitProfileForm';
import { CertificateUploadDialog } from '@/components/CertificateUploadDialog';
import { toApiError } from '@/api/client';
import { initialOnboardingCopy } from '@/lib/signupProduct';

export const Route = createFileRoute('/_authenticated/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isDeveloper = user?.persona === 'Developer';
  const copy = initialOnboardingCopy(user?.persona);
  const steps = [copy.firstStep, 'Subir certificado', 'Listo'];
  const [step, setStep] = useState(0);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const profilesQ = useQuery({
    queryKey: ['cuit-profiles'],
    queryFn: () => cuitProfilesApi.listCuitProfiles(0, 5),
    enabled: !isDeveloper,
  });

  // Si el tenant ya tiene profiles, no es la primera vez — saltamos al dashboard.
  useEffect(() => {
    if (!isDeveloper && profilesQ.data && profilesQ.data.items.length > 0 && !profileId) {
      navigate({ to: '/dashboard' });
    }
  }, [isDeveloper, profilesQ.data, profileId, navigate]);

  if (isDeveloper) {
    return (
      <DeveloperOnboarding
        onOpenDocs={() => navigate({ to: '/api-docs' })}
        onOpenDashboard={() => navigate({ to: '/dashboard' })}
      />
    );
  }
  if (profilesQ.isLoading) return <LoadingScreen />;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {copy.heading}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {copy.subtitle}
      </Typography>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {copy.activityTitle}
            </Typography>
            <CuitProfileForm
              errorMessage={createError}
              submitting={creating}
              submitLabel="Continuar"
              onSubmit={async (values) => {
                setCreating(true);
                setCreateError(null);
                try {
                  const created = await cuitProfilesApi.registerCuitProfile(values);
                  setProfileId(created.id);
                  queryClient.invalidateQueries({ queryKey: ['cuit-profiles'] });
                  setStep(1);
                } catch (err) {
                  setCreateError(toApiError(err).message);
                } finally {
                  setCreating(false);
                }
              }}
            />
          </CardContent>
        </Card>
      ) : null}

      {step === 1 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Subí el certificado AFIP
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Necesitás el archivo .pfx que generaste en AFIP, más la contraseña que pusiste al exportarlo.
              </Typography>
              <Typography variant="caption">
                ¿No tenés el archivo? Seguí los pasos en{' '}
                <a
                  href="https://github.com/gustavonet/afip/blob/main/docs/cert-acquisition-guide.md"
                  target="_blank"
                  rel="noreferrer"
                >
                  cert-acquisition-guide.md
                </a>
                .
              </Typography>
            </Alert>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={() => setUploadOpen(true)}
            >
              Subir certificado
            </Button>
            <Button sx={{ ml: 1 }} onClick={() => setStep(2)}>
              Saltar este paso
            </Button>
            {profileId ? (
              <CertificateUploadDialog
                open={uploadOpen}
                profileId={profileId}
                onClose={() => setUploadOpen(false)}
                onUploaded={() => {
                  setUploadOpen(false);
                  setStep(2);
                }}
              />
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 1 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Todo listo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tu cuenta está configurada. Desde el dashboard podés gestionar tus CUITs, certificados y credenciales API.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate({ to: '/dashboard' })}>
              Ir al dashboard
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
}

function DeveloperOnboarding({
  onOpenDocs,
  onOpenDashboard,
}: {
  onOpenDocs: () => void;
  onOpenDashboard: () => void;
}): JSX.Element {
  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Tu API Sandbox está lista
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        No necesitás cargar un CUIT para empezar. Probá la integración y sumá a un contador cuando quieras operar contra AFIP.
      </Typography>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Próximos pasos
          </Typography>
          <Stepper orientation="vertical" sx={{ mb: 2 }}>
            <Step active>
              <StepLabel>Leer la documentación y probar un request</StepLabel>
            </Step>
            <Step active>
              <StepLabel>Invitar al contador que habilitará el CUIT</StepLabel>
            </Step>
            <Step active>
              <StepLabel>Pasar de Sandbox a producción cuando estés listo</StepLabel>
            </Step>
          </Stepper>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" endIcon={<ArrowForward />} onClick={onOpenDocs}>
              Abrir documentación API
            </Button>
            <Button variant="outlined" onClick={onOpenDashboard}>
              Ir al dashboard
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
